import { Check, Minus, Loader2, Search, X } from 'lucide-react'
import { Button } from '@/shared/ui/Button'
import { getApiError } from '@/api/client'
import { LEVELS, type Level, type Capability } from './rolesUtils'

export const CAP_LABEL: Record<Capability, string> = { view: 'View', edit: 'Add / change', delete: 'Delete' }
export const CAP_ON: Record<Capability, string> = {
  view: 'bg-blue-50 text-blue-700',
  edit: 'bg-amber-50 text-amber-700',
  delete: 'bg-red-50 text-red-700',
}

export const LEVEL_ON: Record<Level, string> = {
  none: 'bg-gray-500 text-white',
  view: 'bg-blue-500 text-white',
  edit: 'bg-amber-500 text-white',
  full: 'bg-green-600 text-white',
}

export const LEVEL_LABEL: Record<Level | 'custom', string> = {
  none: 'No access', view: 'View', edit: 'Edit', full: 'Full', custom: 'Custom',
}

/** None / View / Edit / Full — one choice per area (or per category, or for everything). */
export function LevelPills({ active, onPick, small }: { active?: Level | 'custom'; onPick: (l: Level) => void; small?: boolean }) {
  return (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white font-semibold shrink-0">
      {LEVELS.map(l => (
        <button key={l.key} title={l.hint} onClick={(e) => { e.stopPropagation(); onPick(l.key) }}
          className={`${small ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'} transition-colors ${active === l.key ? LEVEL_ON[l.key] : 'text-gray-600 hover:bg-gray-50'}`}>{l.label}</button>
      ))}
    </div>
  )
}

export function Box({ state, onClick, small }: { state: 'none' | 'some' | 'all'; onClick?: () => void; small?: boolean }) {
  const size = small ? 'w-4 h-4' : 'w-[18px] h-[18px]'
  const cls = state === 'none' ? 'bg-white border-gray-300' : 'bg-brand-500 border-brand-500 text-white'
  return (
    <span role={onClick ? 'checkbox' : undefined} aria-checked={state === 'all' ? true : state === 'some' ? 'mixed' : false}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick() } : undefined}
      className={`${size} shrink-0 rounded border flex items-center justify-center ${cls} ${onClick ? 'cursor-pointer' : ''}`}>
      {state === 'all' && <Check size={11} />}{state === 'some' && <Minus size={11} />}
    </span>
  )
}

export function SearchBox({ value, onChange, placeholder, className = '' }: { value: string; onChange: (v: string) => void; placeholder: string; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
      {value && <button onClick={() => onChange('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>}
    </div>
  )
}

export function ErrorNote({ err, onRetry }: { err: unknown; onRetry: () => void }) {
  return (
    <div className="py-8 text-center space-y-3">
      <p className="text-sm text-red-600 whitespace-pre-line">{getApiError(err)}</p>
      <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>
    </div>
  )
}

export const Center = () => <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-500" size={22} /></div>
